/**
 * ConciergeMessage Component
 *
 * Renders a single chat message bubble for the FlowInCash concierge.
 * User messages: right-aligned, primary brand colour.
 * Assistant messages: left-aligned, neutral background.
 * Supports structured data rendering in a formatted block.
 */

import React from 'react';
import { Box, Typography } from '@mui/material';

export interface ConciergeMessageProps {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  timestamp: number;
  data?: unknown;
}

function formatTimestamp(ts: number): string {
  // Use explicit locale + hour12:false for consistent HH:MM output across environments
  return new Date(ts).toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

function renderStructuredData(data: unknown): string {
  if (data === null || data === undefined) return '';
  return typeof data === 'object' ? JSON.stringify(data) : String(data);
}

export const ConciergeMessage: React.FC<ConciergeMessageProps> = ({
  role,
  text,
  timestamp,
  data,
}) => {
  const isUser = role === 'user';

  return (
    <Box
      data-role={role}
      className={isUser ? 'message-user' : 'message-assistant'}
      sx={{
        display: 'flex',
        flexDirection: isUser ? 'row-reverse' : 'row',
        alignItems: 'flex-start',
        gap: 1,
        mb: 1.5,
      }}
    >
      <Box
        sx={{
          maxWidth: '80%',
          bgcolor: isUser ? 'primary.main' : 'grey.100',
          color: isUser ? 'primary.contrastText' : 'text.primary',
          borderRadius: 2,
          px: 2,
          py: 1,
        }}
      >
        {text !== '' && (
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
            {text}
          </Typography>
        )}

        {data !== undefined && data !== null && (
          <Box
            component="pre"
            sx={{
              mt: text !== '' ? 1 : 0,
              p: 1,
              bgcolor: 'rgba(0,0,0,0.06)',
              borderRadius: 1,
              fontSize: '0.75rem',
              overflowX: 'auto',
              m: 0,
              fontFamily: 'monospace',
            }}
          >
            {renderStructuredData(data)}
          </Box>
        )}

        <Typography
          variant="caption"
          sx={{
            display: 'block',
            color: isUser ? 'rgba(255,255,255,0.7)' : 'text.secondary',
            fontSize: '0.7rem',
            mt: 0.5,
            textAlign: isUser ? 'right' : 'left',
          }}
        >
          {formatTimestamp(timestamp)}
        </Typography>
      </Box>
    </Box>
  );
};

export default ConciergeMessage;
